import RequestReview from '../components/RequestReview';
import React, { useState, useEffect } from 'react';
import { get, post, put, del } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import AttendanceRequests from '../components/AttendanceRequests';

const STATUS_COLORS = {
  pending: { bg:'#FFF3DC', color:'#8B5E0A' },
  approved: { bg:'#E8F5EB', color:'#2E6B3A' },
  rejected: { bg:'#FDEAEA', color:'#8F2E24' },
};

const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];

export default function Leave() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'super_admin';
  const isSuperAdmin = user?.role === 'super_admin';
  const [activeTab, setActiveTab] = useState('my-leave');
  const [requests, setRequests] = useState([]);
  const [balances, setBalances] = useState([]);
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [calendar, setCalendar] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showRequest, setShowRequest] = useState(false);
  const [showTypeModal, setShowTypeModal] = useState(false);
  const [editType, setEditType] = useState(null);
  const [calMonth, setCalMonth] = useState(new Date().getMonth()+1);
  const [calYear, setCalYear] = useState(new Date().getFullYear());
  const [rejectModal, setRejectModal] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const load = () => {
    setLoading(true);
    Promise.all([
      get('/leave'),
      get('/leave/balances'),
      get('/leave/types'),
      get('/leave/calendar?month='+calMonth+'&year='+calYear)
    ]).then(([reqs, bals, types, cal]) => {
      setRequests(reqs.data||[]);
      setBalances(bals.data||[]);
      setLeaveTypes(types.data||[]);
      setCalendar(cal.data||[]);
    }).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [calMonth, calYear]);

  const handleApprove = async (id) => {
    if (!window.confirm('Approve this leave request?')) return;
    try { await put('/leave/'+id+'/approve', {}); load(); }
    catch (err) { alert(err.message); }
  };

  const handleReject = async () => {
    try {
      await put('/leave/'+rejectModal+'/reject', { rejection_reason: rejectReason });
      setRejectModal(null); setRejectReason(''); load();
    } catch (err) { alert(err.message); }
  };

  const handleCancel = async (id) => {
    if (!window.confirm('Cancel this leave request?')) return;
    try { await del('/leave/'+id); load(); }
    catch (err) { alert(err.message); }
  };

  const pendingRequests = requests.filter(r => r.status === 'pending');

  return (
    <div>
      <div className="flex-between mb-lg">
        <div>
          <h2 className="page-title">Leave Management</h2>
          <p className="text-muted text-sm" style={{marginTop:4}}>Manage staff leave requests and balances</p>
        </div>
        <div style={{display:'flex',gap:8}}>
          {isSuperAdmin && (
            <button className="btn btn-outline btn-sm" onClick={()=>{setEditType(null);setShowTypeModal(true);}}>
              ⚙ Configure Leave Types
            </button>
          )}
          <button className="btn btn-primary" onClick={() => setShowRequest(true)}>+ Request Leave</button>
        </div>
      </div>

      {/* Pending approvals banner for admin */}
      {isAdmin && pendingRequests.length > 0 && (
        <div style={{background:'#FFF3DC',border:'1px solid #F5C842',borderRadius:8,
          padding:'12px 20px',marginBottom:16,display:'flex',justifyContent:'space-between',alignItems:'center'}}>
          <div style={{fontSize:13,fontWeight:500,color:'#8B5E0A'}}>
            📋 <strong>{pendingRequests.length}</strong> leave request{pendingRequests.length!==1?'s':''} awaiting approval
          </div>
          <button className="btn btn-sm" style={{background:'#8B5E0A',color:'#fff',border:'none'}}
            onClick={()=>setActiveTab('all-requests')}>Review</button>
        </div>
      )}

      {/* Tabs */}
      <div style={{display:'flex',gap:6,marginBottom:20,borderBottom:'1px solid var(--border-light)',paddingBottom:12}}>
        {[
          ['my-leave','My Leave'],
          ['absence','Absence / Coming Late'],
          ['balances','My Balance'],
          ['calendar','Calendar'],
          ...(isAdmin ? [['all-requests','All Requests'],['all-balances','Staff Balances']] : [])
        ].map(([key,label]) => (
          <button key={key} className={'btn btn-sm '+(activeTab===key?'btn-primary':'btn-ghost')}
            onClick={()=>setActiveTab(key)}>{label}</button>
        ))}
      </div>

      {loading ? <div className="spinner"></div> : (
        <>
          {/* MY LEAVE */}
          {activeTab==='my-leave' && (
            <div>
              {requests.filter(r=>r.user_id===user?.id).length === 0 ? (
                <div className="empty-state"><h3>No leave requests</h3><p>Click + Request Leave to submit your first request.</p></div>
              ) : (
                requests.filter(r=>r.user_id===user?.id).map(r => (
                  <LeaveCard key={r.id} request={r} isAdmin={isAdmin}
                    onApprove={handleApprove}
                    onReject={id=>{setRejectModal(id);setRejectReason('');}}
                    onRefresh={load} onCancel={handleCancel} userId={user?.id} />
                ))
              )}
            </div>
          )}

          {activeTab==='absence' && <AttendanceRequests />}

          {/* MY BALANCE */}
          {activeTab==='balances' && (
            <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(200px,1fr))',gap:12}}>
              {balances.map(b => (
                <div key={b.id} style={{background:'#fff',border:'1px solid var(--border-light)',
                  borderRadius:10,padding:16,boxShadow:'var(--shadow)'}}>
                  <div style={{fontSize:13,fontWeight:600,color:'#3B2A21',marginBottom:12}}>{b.leave_type_name}</div>
                  <div style={{display:'flex',justifyContent:'space-between',marginBottom:6,fontSize:12}}>
                    <span style={{color:'#8A8A8A'}}>Allocated</span>
                    <span style={{fontWeight:600}}>{b.days_allocated} days</span>
                  </div>
                  <div style={{display:'flex',justifyContent:'space-between',marginBottom:6,fontSize:12}}>
                    <span style={{color:'#8A8A8A'}}>Used</span>
                    <span style={{color:'#8F2E24',fontWeight:600}}>{b.days_used} days</span>
                  </div>
                  <div style={{display:'flex',justifyContent:'space-between',marginBottom:10,fontSize:12}}>
                    <span style={{color:'#8A8A8A'}}>Remaining</span>
                    <span style={{color:'#2E6B3A',fontWeight:700}}>{b.days_remaining} days</span>
                  </div>
                  <div style={{height:6,background:'#F2EFE7',borderRadius:3}}>
                    <div style={{height:'100%',borderRadius:3,background:b.days_remaining>5?'#556B2F':b.days_remaining>0?'#8B5E0A':'#8F2E24',
                      width:(b.days_allocated>0?Math.min(100,(b.days_used/b.days_allocated)*100):0)+'%'}}></div>
                  </div>
                  {b.requires_document && (
                    <div style={{fontSize:10,color:'#8A8A8A',marginTop:6}}>📎 {b.document_label} required</div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* CALENDAR */}
          {activeTab==='calendar' && (
            <div>
              <div style={{display:'flex',gap:10,alignItems:'center',marginBottom:16}}>
                <select className="form-control" style={{width:'auto'}} value={calMonth} onChange={e=>setCalMonth(e.target.value)}>
                  {MONTHS.map((m,i)=><option key={i} value={i+1}>{m}</option>)}
                </select>
                <select className="form-control" style={{width:'auto'}} value={calYear} onChange={e=>setCalYear(e.target.value)}>
                  {[2024,2025,2026,2027].map(y=><option key={y} value={y}>{y}</option>)}
                </select>
              </div>
              {calendar.length === 0 ? (
                <div className="empty-state"><h3>No approved leave this month</h3></div>
              ) : (
                <table style={{width:'100%',borderCollapse:'collapse',fontSize:13}}>
                  <thead><tr style={{background:'#556B2F'}}>
                    {['Staff','Leave Type','From','To','Days','Paid'].map(h=>(
                      <th key={h} style={{padding:'10px 12px',textAlign:'left',fontSize:9,color:'#F2EFE7',textTransform:'uppercase'}}>{h}</th>
                    ))}
                  </tr></thead>
                  <tbody>
                    {calendar.map((r,i) => (
                      <tr key={r.id} style={{background:i%2===0?'#fff':'#F9F9F9'}}>
                        <td style={{padding:'9px 12px',borderBottom:'1px solid #EDE7D8',fontWeight:500}}>{r.staff_name}</td>
                        <td style={{padding:'9px 12px',borderBottom:'1px solid #EDE7D8'}}>{r.leave_type_name}</td>
                        <td style={{padding:'9px 12px',borderBottom:'1px solid #EDE7D8'}}>{r.start_date}</td>
                        <td style={{padding:'9px 12px',borderBottom:'1px solid #EDE7D8'}}>{r.end_date}</td>
                        <td style={{padding:'9px 12px',borderBottom:'1px solid #EDE7D8',textAlign:'center',fontWeight:600}}>{r.days_requested}</td>
                        <td style={{padding:'9px 12px',borderBottom:'1px solid #EDE7D8'}}>
                          <span style={{fontSize:10,fontWeight:700,padding:'2px 8px',borderRadius:20,
                            background:r.is_paid?'#E8F5EB':'#FDEAEA',color:r.is_paid?'#2E6B3A':'#8F2E24'}}>
                            {r.is_paid?'Paid':'Unpaid'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}

          {/* ALL REQUESTS - admin */}
          {activeTab==='all-requests' && isAdmin && (
            <div>
              {requests.length === 0 ? (
                <div className="empty-state"><h3>No leave requests</h3></div>
              ) : (
                requests.map(r => (
                  <LeaveCard key={r.id} request={r} isAdmin={isAdmin}
                    onApprove={handleApprove}
                    onReject={id=>{setRejectModal(id);setRejectReason('');}}
                    onRefresh={load} onCancel={handleCancel} userId={user?.id} showStaffName />
                ))
              )}
            </div>
          )}

          {/* ALL BALANCES - admin */}
          {activeTab==='all-balances' && isAdmin && (
            <AllStaffBalances />
          )}
        </>
      )}

      {/* Reject modal */}
      {rejectModal && (
        <div className="modal-overlay">
          <div className="modal" style={{maxWidth:420}}>
            <div className="modal-hdr">
              <span className="modal-title">Reject Leave Request</span>
              <button className="btn btn-ghost btn-sm" onClick={()=>setRejectModal(null)}>X</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label className="form-label">Reason for Rejection *</label>
                <textarea className="form-control" rows={3} value={rejectReason}
                  onChange={e=>setRejectReason(e.target.value)}
                  placeholder="Explain why this leave request is being rejected..." />
              </div>
            </div>
            <div className="modal-ftr">
              <button className="btn btn-outline" onClick={()=>setRejectModal(null)}>Cancel</button>
              <button className="btn btn-danger" onClick={handleReject} disabled={!rejectReason}>Reject</button>
            </div>
          </div>
        </div>
      )}

      {showRequest && (
        <RequestLeaveModal leaveTypes={leaveTypes} onClose={()=>setShowRequest(false)}
          onSaved={()=>{setShowRequest(false);load();}} />
      )}

      {showTypeModal && (
        <LeaveTypeModal type={editType} onClose={()=>{setShowTypeModal(false);setEditType(null);}}
          onSaved={()=>{setShowTypeModal(false);setEditType(null);load();}} />
      )}
    </div>
  );
}

function LeaveCard({ request: r, isAdmin, onApprove, onReject, onCancel, userId, showStaffName, onRefresh }) {
  const sc = STATUS_COLORS[r.status] || STATUS_COLORS.pending;
  return (
    <div style={{background:'#fff',border:'1px solid var(--border-light)',borderRadius:10,
      padding:16,marginBottom:12,boxShadow:'var(--shadow)'}}>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:10}}>
        <div>
          {showStaffName && <div style={{fontSize:13,fontWeight:700,color:'#3B2A21',marginBottom:2}}>{r.staff_name}</div>}
          <div style={{fontSize:14,fontWeight:600,color:'#556B2F'}}>{r.leave_type_name}</div>
          <div style={{fontSize:12,color:'#8A8A8A',marginTop:2}}>
            {r.start_date} → {r.end_date} &bull; <strong>{r.days_requested} working day{r.days_requested!==1?'s':''}</strong>
          </div>
          {r.reason && <div style={{fontSize:12,color:'#555',marginTop:4}}>{r.reason}</div>}
        </div>
        <div style={{display:'flex',gap:8,alignItems:'center'}}>
          <span style={{padding:'4px 12px',borderRadius:20,fontSize:10,fontWeight:700,
            textTransform:'uppercase',background:sc.bg,color:sc.color}}>{r.status}</span>
          {r.document_filename && (
            <a href={'/api/leave/documents/'+r.document_filename} target="_blank" rel="noreferrer"
              className="btn btn-ghost btn-sm" style={{fontSize:10}}>📎 Document</a>
          )}
        </div>
      </div>
      <div style={{display:'flex',gap:8,justifyContent:'flex-end'}}>
        <RequestReview kind="leave" id={r.id} onRefresh={onRefresh}/>
        {r.status==='rejected' && r.rejection_reason && (
          <span style={{fontSize:11,color:'#8F2E24'}}>Reason: {r.rejection_reason}</span>
        )}
      </div>
    </div>
  );
}

function AllStaffBalances() {
  const [balances, setBalances] = useState([]);
  const [year, setYear] = useState(new Date().getFullYear());
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    get('/leave/balances/all?year='+year).then(d=>setBalances(d.data||[])).finally(()=>setLoading(false));
  }, [year]);

  // Group by staff
  const grouped = balances.reduce((acc, b) => {
    if (!acc[b.staff_name]) acc[b.staff_name] = [];
    acc[b.staff_name].push(b);
    return acc;
  }, {});

  return (
    <div>
      <div style={{display:'flex',gap:10,marginBottom:16,alignItems:'center'}}>
        <select className="form-control" style={{width:'auto'}} value={year} onChange={e=>setYear(e.target.value)}>
          {[2024,2025,2026,2027].map(y=><option key={y}>{y}</option>)}
        </select>
        <span style={{fontSize:12,color:'#8A8A8A'}}>{Object.keys(grouped).length} staff members</span>
      </div>
      {loading ? <div className="spinner"></div> : (
        Object.entries(grouped).map(([name, bals]) => (
          <div key={name} style={{background:'#fff',border:'1px solid var(--border-light)',borderRadius:10,
            marginBottom:12,overflow:'hidden'}}>
            <div style={{background:'#F2EFE7',padding:'10px 16px',fontWeight:600,fontSize:13,color:'#3B2A21'}}>
              {name}
            </div>
            <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(150px,1fr))',gap:0}}>
              {bals.map(b => (
                <div key={b.id} style={{padding:'12px 16px',borderRight:'1px solid #F2EFE7'}}>
                  <div style={{fontSize:11,color:'#8A8A8A',marginBottom:4}}>{b.leave_type_name}</div>
                  <div style={{fontSize:12}}>
                    <span style={{color:'#2E6B3A',fontWeight:700}}>{b.days_remaining}</span>
                    <span style={{color:'#8A8A8A'}}> / {b.days_allocated} days</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))
      )}
    </div>
  );
}

function RequestLeaveModal({ leaveTypes, onClose, onSaved }) {
  const {user}=useAuth();
  const [form, setForm] = useState({ leave_type_id:'', start_date:'', end_date:'', reason:'' });
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [days, setDays] = useState(0);

  const selectedType = leaveTypes.find(lt => lt.id === form.leave_type_id);

  // Calculate working days
  useEffect(() => {
    if (!form.start_date || !form.end_date) { setDays(0); return; }
    const start = new Date(form.start_date);
    const end = new Date(form.end_date);
    if (end < start) { setDays(0); return; }
    let count = 0;
    let current = new Date(start);
    while (current <= end) {
      const day = current.getDay();
      if (day !== 0 && day !== 6) count++;
      current.setDate(current.getDate() + 1);
    }
    setDays(count);
  }, [form.start_date, form.end_date]);

  const submit = async () => {
    if (!form.leave_type_id || !form.start_date || !form.end_date) {
      setError('Please fill all required fields'); return;
    }
    if(form.end_date<form.start_date){setError('End date cannot be before start date.');return;}
    if(form.start_date.slice(0,4)!==form.end_date.slice(0,4)){
      setError('Submit separate requests for each calendar year.');return;
    }
    if(days<1){setError('Select dates containing working days.');return;}
    if(!selectedType?.is_active){setError('Choose an active leave type.');return;}
    if(selectedType.requires_document&&!file){
      setError((selectedType.document_label||'Supporting document')+' is required.');return;
    }
    setSaving(true); setError('');
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k,v]) => fd.append(k, v));
      if (file) fd.append('document', file);
      const token = localStorage.getItem('bb_token');
      const res = await fetch('/api/leave', {
        method:'POST', headers:{ Authorization:'Bearer '+token }, body: fd
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      onSaved();
    } catch (err) { setError(err.message); } finally { setSaving(false); }
  };

  return (
    <div className="modal-overlay">
      <div className="modal" style={{maxWidth:500}}>
        <div className="modal-hdr">
          <span className="modal-title">Request Leave</span>
          <button className="btn btn-ghost btn-sm" disabled={saving} onClick={onClose}>X</button>
        </div>
        <div className="modal-body">
          {error && <div className="alert alert-error mb-md">{error}</div>}
          <div className="form-group">
            <label className="form-label">Leave Type *</label>
            <select className="form-control" value={form.leave_type_id}
              onChange={e=>{setForm({...form,leave_type_id:e.target.value});setFile(null);}}>
              <option value="">Select leave type</option>
              {leaveTypes.filter(lt=>lt.is_active&&(!lt.eligible_roles?.length||lt.eligible_roles.includes(user?.role))).map(lt=>(
                <option key={lt.id} value={lt.id}>
                  {lt.name} ({lt.days_allowed} days/year{lt.min_months_service>0?' — '+lt.min_months_service+' months service required':''})
                </option>
              ))}
            </select>
          </div>
          {selectedType?.min_months_service > 0 && (
            <div style={{background:'#FFF3DC',borderRadius:6,padding:'8px 12px',fontSize:12,color:'#8B5E0A',marginBottom:12}}>
              ⚠ This leave type requires <strong>{selectedType.min_months_service} months</strong> of service before you can apply.
            </div>
          )}
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Start Date *</label>
              <input type="date" className="form-control" value={form.start_date}
                onChange={e=>setForm({...form,start_date:e.target.value})} />
            </div>
            <div className="form-group">
              <label className="form-label">End Date *</label>
              <input type="date" min={form.start_date||undefined} className="form-control" value={form.end_date}
                onChange={e=>setForm({...form,end_date:e.target.value})} />
            </div>
          </div>
          {days > 0 && (
            <div style={{background:'#E8F5EB',borderRadius:6,padding:'8px 12px',fontSize:13,
              color:'#2E6B3A',fontWeight:500,marginBottom:12}}>
              📅 {days} weekday{days!==1?'s':''} selected. The server checks public holidays and your work schedule.
            </div>
          )}
          <div className="form-group">
            <label className="form-label">Reason</label>
            <textarea className="form-control" rows={2} value={form.reason}
              onChange={e=>setForm({...form,reason:e.target.value})}
              placeholder="Brief reason for leave..." />
          </div>
          {selectedType?.requires_document && (
            <div className="form-group">
              <label className="form-label">{selectedType.document_label} * (Required)</label>
              <input key={form.leave_type_id} type="file" className="form-control" onChange={e=>setFile(e.target.files[0])} />
              <div className="form-hint">Please upload your {selectedType.document_label}</div>
            </div>
          )}
        </div>
        <div className="modal-ftr">
          <button className="btn btn-outline" disabled={saving} onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={submit} disabled={saving}>
            {saving?'Submitting...':'Submit Request'}
          </button>
        </div>
      </div>
    </div>
  );
}

function LeaveTypeModal({ type, onClose, onSaved }) {
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [editingType, setEditingType] = useState(type||null);
  const [showForm, setShowForm] = useState(!!type);
  const [form, setForm] = useState({
    name: type?.name||'',
    days_allowed: type?.days_allowed||0,
    is_paid: type?.is_paid!==false,
    requires_document: type?.requires_document||false,
    document_label: type?.document_label||'Supporting Document',
    requires_approval: type?.requires_approval!==false,
    min_months_service: type?.min_months_service||0,
    is_active: type?.is_active!==false,
    eligible_roles: type?.eligible_roles||[]
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    get('/leave/types').then(d => setLeaveTypes(d.data||[]));
  }, []);

  const startEdit = (lt) => {
    setEditingType(lt);
    setForm({
      name: lt.name||'',
      days_allowed: lt.days_allowed||0,
      is_paid: lt.is_paid!==false,
      requires_document: lt.requires_document||false,
      document_label: lt.document_label||'Supporting Document',
      requires_approval: lt.requires_approval!==false,
      min_months_service: lt.min_months_service||0,
      is_active: lt.is_active!==false,
      eligible_roles: lt.eligible_roles||[]
    });
    setShowForm(true);
  };

  const startNew = () => {
    setEditingType(null);
    setForm({ name:'', days_allowed:0, is_paid:true, requires_document:false,
      document_label:'Supporting Document', requires_approval:true, min_months_service:0, is_active:true, eligible_roles:[] });
    setShowForm(true);
  };

  const deactivate = async (lt) => {
    if (!window.confirm('Deactivate '+lt.name+'? Staff will no longer be able to apply for this leave type.')) return;
    try {
      await put('/leave/types/'+lt.id, { ...lt, is_active: false });
      get('/leave/types').then(d => setLeaveTypes(d.data||[]));
    } catch (err) { alert(err.message); }
  };

  const reactivate = async (lt) => {
    try {
      await put('/leave/types/'+lt.id, { ...lt, is_active: true });
      get('/leave/types').then(d => setLeaveTypes(d.data||[]));
    } catch (err) { alert(err.message); }
  };

  const submit = async () => {
    if (!form.name) { setError('Name required'); return; }
    setSaving(true); setError('');
    try {
      if (editingType) await put('/leave/types/'+editingType.id, form);
      else await post('/leave/types', form);
      setShowForm(false);
      get('/leave/types').then(d => setLeaveTypes(d.data||[]));
      onSaved();
    } catch (err) { setError(err.message); } finally { setSaving(false); }
  };

  return (
    <div className="modal-overlay">
      <div className="modal" style={{maxWidth:580}}>
        <div className="modal-hdr">
          <span className="modal-title">
            {showForm ? (editingType?'Edit: '+editingType.name:'New Leave Type') : '⚙ Configure Leave Types'}
          </span>
          <div style={{display:'flex',gap:6}}>
            {showForm && <button className="btn btn-ghost btn-sm" onClick={()=>setShowForm(false)}>← Back</button>}
            <button className="btn btn-ghost btn-sm" onClick={onClose}>X</button>
          </div>
        </div>
        <div className="modal-body">
          {!showForm ? (
            <div>
              <div style={{display:'flex',justifyContent:'flex-end',marginBottom:12}}>
                <button className="btn btn-primary btn-sm" onClick={startNew}>+ Add New Leave Type</button>
              </div>
              {leaveTypes.length === 0 ? (
                <div style={{textAlign:'center',color:'#8A8A8A',padding:20}}>No leave types configured yet</div>
              ) : (
                <div>
                  {leaveTypes.map(lt => (
                    <div key={lt.id} style={{display:'flex',justifyContent:'space-between',alignItems:'center',
                      padding:'12px 14px',borderRadius:8,marginBottom:8,
                      background:lt.is_active?'#fff':'#F9F9F9',
                      border:'1px solid '+(lt.is_active?'var(--border-light)':'#DDD'),
                      opacity:lt.is_active?1:0.6}}>
                      <div>
                        <div style={{fontSize:13,fontWeight:600,color:'#3B2A21',marginBottom:2}}>
                          {lt.name}
                          {!lt.is_active && <span style={{fontSize:10,marginLeft:8,color:'#8A8A8A',fontWeight:400}}>(Inactive)</span>}
                        </div>
                        <div style={{fontSize:11,color:'#8A8A8A'}}>
                          {lt.days_allowed > 0 ? lt.days_allowed+' days/year' : 'Unlimited'}
                          {' • '}{lt.is_paid?'Paid':'Unpaid'}
                          {lt.requires_document?' • '+lt.document_label:''}
                          {lt.min_months_service>0?' • '+lt.min_months_service+' months service':''}
                        </div>
                      </div>
                      <div style={{display:'flex',gap:6}}>
                        <button className="btn btn-outline btn-sm" style={{fontSize:11}}
                          onClick={()=>startEdit(lt)}>Edit</button>
                        {lt.is_active ? (
                          <button className="btn btn-ghost btn-sm" style={{fontSize:11,color:'#8F2E24'}}
                            onClick={()=>deactivate(lt)}>Deactivate</button>
                        ) : (
                          <button className="btn btn-ghost btn-sm" style={{fontSize:11,color:'#2E6B3A'}}
                            onClick={()=>reactivate(lt)}>Activate</button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div>
              {error && <div className="alert alert-error mb-md">{error}</div>}
              <div className="form-group">
                <label className="form-label">Leave Type Name *</label>
                <input className="form-control" value={form.name}
                  onChange={e=>setForm({...form,name:e.target.value})}
                  placeholder="e.g. Annual Leave" />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Days Allowed Per Year</label>
                  <input type="number" className="form-control" value={form.days_allowed}
                    onChange={e=>setForm({...form,days_allowed:e.target.value})} />
                  <div className="form-hint">Set 0 for unlimited or unpaid leave</div>
                </div>
                <div className="form-group">
                  <label className="form-label">Minimum Months of Service</label>
                  <input type="number" className="form-control" value={form.min_months_service}
                    onChange={e=>setForm({...form,min_months_service:e.target.value})} />
                  <div className="form-hint">0 = available from day one</div>
                </div>
              </div>
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12,marginBottom:12}}>
                {[['is_paid','Paid Leave'],['requires_approval','Requires Approval'],
                  ['requires_document','Requires Document'],['is_active','Active']].map(([key,label])=>(
                  <label key={key} style={{display:'flex',alignItems:'center',gap:8,fontSize:13,cursor:'pointer',
                    padding:'8px 12px',borderRadius:8,background:'#F2EFE7'}}>
                    <input type="checkbox" checked={form[key]}
                      onChange={e=>setForm({...form,[key]:e.target.checked})} />
                    {label}
                  </label>
                ))}
              </div>
              {form.requires_document && (
                <div className="form-group">
                  <label className="form-label">Document Label</label>
                  <input className="form-control" value={form.document_label}
                    onChange={e=>setForm({...form,document_label:e.target.value})}
                    placeholder="e.g. Medical Certificate" />
                </div>
              )}
            </div>
          )}
        </div>
        {showForm && (
          <div className="modal-ftr">
            <button className="btn btn-outline" onClick={()=>setShowForm(false)}>Cancel</button>
            <button className="btn btn-primary" onClick={submit} disabled={saving}>
              {saving?'Saving...':editingType?'Save Changes':'Create Leave Type'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
